/**
 * Frontend Sentry init. Per Core ADR 0025, Senviro registers its own self-host
 * Sentry org / project; DSN provisioning lands in W10-F5-S1. This step ships
 * the SDK + the env-var hook only — calling `initSentry()` with no DSN is a
 * no-op so dev runs without Sentry don't error.
 *
 * Bundle gating: `@sentry/react` is loaded lazily so dev/test runs that don't
 * configure a DSN do not pull the SDK into the initial chunk.
 *
 * **W95-F3-S2 — vitest seam.** Env reads use STATIC
 * `import.meta.env.<KEY>` references (not the previous
 * `const env = import.meta.env; env.KEY` aliasing) so vitest's `define`
 * block in `apps/web/vitest.config.ts` can rewrite them to global
 * symbols at test time. Tests then call
 * `vi.stubGlobal('__VITE_SENTRY_DSN__', '...')` to swap values
 * per-test. Production Vite build is untouched (`define` is scoped to
 * `test:` only) — vite inlines `import.meta.env.VITE_*` from the
 * build env as before.
 */

interface SentryEvent {
  request?: {
    headers?: Record<string, unknown>;
  };
  user?: { email?: string };
}

interface SentrySdkLike {
  captureException: (err: unknown, ctx?: { tags?: Record<string, unknown> }) => string | void;
  addBreadcrumb: (crumb: {
    category?: string;
    message?: string;
    data?: Record<string, unknown>;
    level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';
  }) => void;
}

/**
 * Strips known PII before Sentry ships the event off-host. Currently scrubs:
 *   - Authorization / Cookie / x-api-key request headers
 *   - user.email (replaced with `[REDACTED]`)
 */
export function stripPII<T extends SentryEvent>(event: T): T {
  if (event.request?.headers) {
    for (const k of Object.keys(event.request.headers)) {
      const lk = k.toLowerCase();
      if (
        lk === 'authorization' ||
        lk === 'cookie' ||
        lk === 'x-api-key' ||
        lk === 'set-cookie'
      ) {
        event.request.headers[k] = '[REDACTED]';
      }
    }
  }
  if (event.user?.email) {
    event.user.email = '[REDACTED]';
  }
  return event;
}

let initialized = false;
let sdkRef: SentrySdkLike | undefined;

export async function initSentry(): Promise<void> {
  if (initialized) return;
  // W95-F3-S2 — STATIC env reads (one per identifier) so vitest's
  // `define` rewrite in `apps/web/vitest.config.ts` can intercept each
  // value individually. DO NOT alias `import.meta.env` to a local — the
  // alias breaks the static reference vite scans for.
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    // No-op when DSN missing — keeps dev runs silent.
    return;
  }
  const mode = import.meta.env.MODE;
  const release = import.meta.env.VITE_APP_RELEASE ?? 'dev';

  let Sentry: typeof import('@sentry/react') | undefined;
  try {
    Sentry = await import('@sentry/react');
  } catch (err) {
    // SDK not installed — log once and bail.
    console.warn(
      '[sentry] @sentry/react not available; skipping init.',
      err instanceof Error ? err.message : err,
    );
    return;
  }
  if (!Sentry) return;

  Sentry.init({
    dsn,
    environment: mode,
    release,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: true }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
    beforeSend: (event) => stripPII(event as SentryEvent) as typeof event,
  });
  sdkRef = Sentry as unknown as SentrySdkLike;
  initialized = true;
}

/**
 * @internal — used by tests to reset the module-level `initialized`
 * flag between cases so each test sees a clean init state. Production
 * callers should never need this.
 */
export function _resetSentryForTests(): void {
  initialized = false;
  sdkRef = undefined;
}

/**
 * Forward a caught exception to Sentry from anywhere in the FE — outside the
 * render tree where `SentryErrorBoundary` doesn't see it (mutation handlers,
 * query error sinks, manual catch blocks). No-op when Sentry isn't
 * initialized so dev/test runs without a DSN don't pay an SDK cost.
 *
 * Tags should be `snake_case` and low-cardinality to match the backend
 * convention (`SentryScopeContext` in apps/api/src/observability/sentry.ts).
 */
export function captureFrontendException(
  err: unknown,
  tags?: Record<string, string | number | boolean>,
): void {
  if (!initialized || !sdkRef) return;
  try {
    sdkRef.captureException(err, tags ? { tags } : undefined);
  } catch (capErr) {
    console.warn(
      '[sentry] captureException threw, ignoring:',
      capErr instanceof Error ? capErr.message : capErr,
    );
  }
}

/**
 * Drop a breadcrumb on the active Sentry scope. Use for high-signal user
 * actions (route changes, mutation kickoffs) so the next captured error
 * carries useful context. No-op when Sentry isn't initialized.
 */
export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level: 'info' | 'warning' | 'error' = 'info',
): void {
  if (!initialized || !sdkRef) return;
  try {
    sdkRef.addBreadcrumb({ category, message, data, level });
  } catch {
    // breadcrumbs are best-effort — never let them propagate
  }
}

/** @internal — used by tests to assert non-init when DSN absent. */
export function _isSentryInitialized(): boolean {
  return initialized;
}
